import { createSelector } from 'reselect';
import { IntlSelectors, uninitState, loadingState, successState, statusSuccess, statusLoading, statusUninit } from '@grandvisionhq/state';
import { convertToLensTypeInfo, lowToHigh, convertToPackageInfo, convertToLensAddOnInfo } from '../helpers.js';
import { getLensType, getPackage, getAddOn } from '../products/selectors.internal.js';
import { getBundleFrame, getBundleLensTypes, getBundleLensType, getBundlePackages, getBundlePackage, getBundleAddOns } from './selectors.internal.js';

const isDefined = (v)=>typeof v !== 'undefined'
;
const isDefinedAndHasPrice = (v)=>isDefined(v) && isDefined(v.price)
;
/**
 * Checks if the SKU belongs to a master variant. This decides whether the "product" gets
 * its own tile, or that it should be part of a tile as a variant.
 */ const isMasterProduct = (product, sku)=>{
    return (product === null || product === void 0 ? void 0 : product.masterVariant.sku) === sku;
};
/**
 * These are public selectors that are meant to be used from the container components outside of this state dir
 */ const isAnyStillLoading = (resources, skus)=>{
    return [
        ...skus
    ].reduce((isLoading, sku)=>{
        if (isLoading) {
            return isLoading;
        }
        var _sku;
        const resource = (_sku = resources[sku]) !== null && _sku !== void 0 ? _sku : uninitState();
        return statusLoading(resource) || statusUninit(resource);
    }, false);
};
const getLensTypes = createSelector(getBundleFrame, getBundleLensTypes, IntlSelectors.getLocale, (s)=>s
, (bundle, lensTypes, locale, s)=>{
    if (!bundle || !locale) {
        return uninitState();
    }
    const { lensTypeSkus  } = bundle;
    if (isAnyStillLoading(lensTypes, lensTypeSkus)) {
        return loadingState();
    }
    var ref;
    const results = [
        ...lensTypeSkus
    ].map((lensTypeSku)=>{
        const lensType = getLensType(s, {
            lensTypeSku
        });
        if (!lensType) {
            return undefined;
        }
        if (!isMasterProduct(lensType, lensTypeSku)) {
            return undefined;
        }
        return convertToLensTypeInfo(lensType, lensTypeSku, locale);
    }).filter(isDefinedAndHasPrice).sort(lowToHigh((lensType)=>{
        var ref2, ref1;
        return (ref = (ref2 = lensType.price) === null || ref2 === void 0 ? void 0 : ref2.discountedPrice) !== null && ref !== void 0 ? ref : (ref1 = lensType.price) === null || ref1 === void 0 ? void 0 : ref1.originalPrice;
    }));
    return successState(results);
});
const getPackages = createSelector(getBundleLensType, getBundlePackages, IntlSelectors.getLocale, (s)=>s
, (lensType, packages, locale, s)=>{
    if (!lensType || !locale) {
        return uninitState();
    }
    if (!statusSuccess(lensType)) {
        return lensType;
    }
    const { packageSkus  } = lensType.result;
    if (isAnyStillLoading(packages, packageSkus)) {
        return loadingState();
    }
    var ref;
    const results = [
        ...packageSkus
    ].map((packageSku)=>{
        const packageProduct = getPackage(s, {
            packageSku
        });
        if (!packageProduct) {
            return undefined;
        }
        if (!isMasterProduct(packageProduct, packageSku)) {
            return undefined;
        }
        return convertToPackageInfo(packageProduct, packageSku, locale);
    }).filter(isDefinedAndHasPrice).sort(lowToHigh((packageInfo)=>{
        var ref4, ref3;
        return (ref = (ref4 = packageInfo.price) === null || ref4 === void 0 ? void 0 : ref4.discountedPrice) !== null && ref !== void 0 ? ref : (ref3 = packageInfo.price) === null || ref3 === void 0 ? void 0 : ref3.originalPrice;
    }));
    return successState(results);
});
const getLensAddOns = createSelector(getBundlePackage, getBundleAddOns, IntlSelectors.getLocale, (s)=>s
, (bundlePackage, addOns, locale, s)=>{
    if (!bundlePackage || !locale) {
        return uninitState();
    }
    if (!statusSuccess(bundlePackage)) {
        return bundlePackage;
    }
    const { addOnSkus  } = bundlePackage.result;
    if (isAnyStillLoading(addOns, addOnSkus)) {
        return loadingState();
    }
    var ref;
    const results = [
        ...addOnSkus
    ].map((addOnSku)=>{
        const addOnProduct = getAddOn(s, {
            addOnSku
        });
        if (!addOnProduct) {
            return undefined;
        }
        if (!isMasterProduct(addOnProduct, addOnSku)) {
            return undefined;
        }
        return convertToLensAddOnInfo(addOnProduct, addOnSku, locale);
    }).filter(isDefinedAndHasPrice).sort(lowToHigh((addOnInfo)=>{
        var ref6, ref5;
        return (ref = (ref6 = addOnInfo.price) === null || ref6 === void 0 ? void 0 : ref6.discountedPrice) !== null && ref !== void 0 ? ref : (ref5 = addOnInfo.price) === null || ref5 === void 0 ? void 0 : ref5.originalPrice;
    }));
    return successState(results);
});

export { getLensAddOns, getLensTypes, getPackages };
//# sourceMappingURL=selectors.js.map
